/**
 * Shared utilities, styled components, and hooks for both
 * PivotPointBranchView (radial) and PivotPointBranchViewTree.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import { SCENARIO_COLORS_BY_NAME } from '../../config/brandColors';
import isTouchDevice from '../../utils/isTouchDevice';

// --- Styled Components ---

export const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

export const NavOverlay = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  gap: 8px;
  z-index: 10;
`;

export const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.4);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const TooltipBox = styled.div`
  position: fixed;
  background: rgba(10, 10, 10, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  max-width: 300px;
  pointer-events: none;
  z-index: 100;
  color: white;

  .tooltip-title {
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 4px;
  }

  .tooltip-scenario {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 500;
    margin-top: 4px;
  }

  .tooltip-meta {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 4px;
  }
`;

export const EdgeArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$side === 'left' ? 'left: 16px;' : 'right: 16px;'}
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.2;
    cursor: default;
    &:hover {
      background: rgba(0, 0, 0, 0.5);
      border-color: rgba(255, 255, 255, 0.2);
      transform: translateY(-50%);
    }
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const PivotCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 6px 16px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  z-index: 10;
  white-space: nowrap;
`;

export const ZoomControls = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
`;

/** Hide +/- chrome on touch / Tizen — pinch & drag remain. */
export function ZoomControlsPanel({ children }) {
  if (isTouchDevice()) return null;
  return <ZoomControls>{children}</ZoomControls>;
}

export const ZoomButton = styled.button`
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

export const ZoomLevel = styled.div`
  text-align: center;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  padding: 4px;
`;

// --- Helpers ---

export const DEFAULT_LINE_WIDTH = 20;

/**
 * Wrap text into lines. Returns { lines, truncated }.
 * If maxLines is provided and text exceeds it, the last line gets "..." appended.
 */
export function wrapText(text, maxWidth = DEFAULT_LINE_WIDTH, maxLines = null) {
  if (!text) return { lines: [], truncated: false };
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = '';
  let truncated = false;

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
      if (maxLines && lines.length >= maxLines) {
        truncated = true;
        break;
      }
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine && (!maxLines || lines.length < maxLines)) {
    lines.push(currentLine);
  }
  if (truncated && lines.length === maxLines) {
    lines[maxLines - 1] = lines[maxLines - 1] + '...';
  }
  return { lines, truncated };
}

export function getScenarioColor(scenarioName) {
  if (!scenarioName) return '#607d8b';
  return SCENARIO_COLORS_BY_NAME[scenarioName]
    || SCENARIO_COLORS_BY_NAME[scenarioName.toLowerCase()]
    || '#607d8b';
}

export function buildGradientStops(scenarioNames) {
  const colors = scenarioNames.map(n => getScenarioColor(n));
  if (colors.length < 2) return null;
  const step = 100 / (colors.length - 1);
  return colors.map((c, i) => ({ offset: `${Math.round(i * step)}%`, color: c }));
}

export function estimateLabelHeight(text) {
  const { lines } = wrapText(text || '', DEFAULT_LINE_WIDTH, 8);
  return lines.length * 13 + 20;
}

/**
 * Compute a d3 zoom transform that fits a set of points into width x height.
 */
export function computeFitTransform(points, width, height) {
  if (!points.length) return d3.zoomIdentity;
  const minX = Math.min(...points.map(p => p.x));
  const maxX = Math.max(...points.map(p => p.x));
  const minY = Math.min(...points.map(p => p.y));
  const maxY = Math.max(...points.map(p => p.y));
  if (![minX, maxX, minY, maxY, width, height].every(Number.isFinite)) return d3.zoomIdentity;
  const bw = maxX - minX || 1;
  const bh = maxY - minY || 1;
  const rawScale = Math.min(width / bw, height / bh) * 0.9;
  const scale = Math.max(0.3, Math.min(rawScale, 1.5));
  if (!Number.isFinite(scale)) return d3.zoomIdentity;
  const tx = (width - bw * scale) / 2 - minX * scale;
  const ty = (height - bh * scale) / 2 - minY * scale;
  if (![tx, ty].every(Number.isFinite)) return d3.zoomIdentity;
  return d3.zoomIdentity.translate(tx, ty).scale(scale);
}

/**
 * Collect bounding points from a layout with branches and leaves.
 * Works for both the radial and tree layouts.
 */
export function collectBoundsPoints(layout) {
  const points = [];
  // Center node
  points.push({ x: layout.cx - 60, y: layout.cy - layout.centerRadius - 60 });
  points.push({ x: layout.cx + 60, y: layout.cy + layout.centerRadius + 60 });

  const collectFromBranch = (branch) => {
    points.push({ x: branch.x - 60, y: branch.y - (branch.nodeRadius || 20) - 60 });
    points.push({ x: branch.x + 60, y: branch.y + (branch.nodeRadius || 20) + 40 });
    (branch.leaves || []).forEach(leaf => {
      const labelH = estimateLabelHeight(leaf.text);
      points.push({ x: leaf.x - 60, y: leaf.y - (leaf.nodeRadius || 8) - 10 });
      points.push({ x: leaf.x + 60, y: leaf.y + (leaf.nodeRadius || 8) + labelH });
    });
    // Recurse into sub-branches (tree layout has variables as sub-branches)
    (branch.children || []).forEach(child => collectFromBranch(child));
  };

  (layout.branches || []).forEach(collectFromBranch);
  return points;
}

// --- Shared SVG defs ---

export function SvgDefs({ centerGradientStops }) {
  return (
    <defs>
      {centerGradientStops && (
        <linearGradient id="center-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          {centerGradientStops.map((stop, i) => (
            <stop key={i} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
      )}
      <filter id="center-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="6" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <filter id="leaf-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

// --- Adaptive text rendering based on node count and zoom level ---

/**
 * Returns rendering params that scale with density (total leaf count) and zoom.
 * Use in all branch views for consistent behavior:
 *   const textParams = useAdaptiveText(totalLeafCount, zoomLevel);
 *
 * At overview zoom: dense views hide text, sparse views show tiny text
 * At close zoom: all views show readable 12-14px text
 */
export function getAdaptiveTextParams(totalLeaves, zoomLevel) {
  const isDense = totalLeaves > 25;
  const isMedium = totalLeaves > 10;

  // Always show text — but scale font size and truncation with density + zoom
  const leafFontSize = isDense ? 8 : isMedium ? 9 : 10;
  const centerFontSize = 16;
  const centerTitleFontSize = 14;
  const lineHeight = isDense ? 10 : isMedium ? 12 : 13;

  // Truncation: dense views show 1 line zoomed out, more when zoomed in
  const zoomedIn = zoomLevel > 1.3;
  const maxLabelLines = zoomedIn
    ? (isDense ? 4 : 6)
    : (isDense ? 1 : isMedium ? 2 : 4);
  const labelWidth = zoomedIn
    ? DEFAULT_LINE_WIDTH
    : (isDense ? 12 : isMedium ? 14 : DEFAULT_LINE_WIDTH);

  const showLeafText = true; // always show

  const leafRadius = isDense ? 4 : isMedium ? 6 : 8;

  return {
    leafFontSize, centerFontSize, centerTitleFontSize,
    lineHeight, maxLabelLines, labelWidth,
    showLeafText, leafRadius,
    isDense, isMedium,
  };
}

// --- Custom hook for D3 zoom with auto-fit ---

export function useZoomToFit({ pivotId, layout, width, height }) {
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const lastZoomTargetRef = useRef(null);

  // Setup D3 zoom
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const zoomableContent = svg.select('.zoomable-content');

    const zoom = d3.zoom()
      .scaleExtent([0.3, 4])
      .on('zoom', (event) => {
        setZoomLevel(event.transform.k);
        zoomableContent.attr('transform', event.transform);
      });

    svg.call(zoom);
    zoomRef.current = zoom;
    svg.on('dblclick.zoom', null);

    return () => { svg.on('.zoom', null); };
  }, []);

  // Auto zoom-to-fit on pivot change
  useEffect(() => {
    if (!svgRef.current || !zoomRef.current || !layout) return;
    const points = collectBoundsPoints(layout);
    if (!points.length) return;
    const transform = computeFitTransform(points, width, height);
    const svg = d3.select(svgRef.current);
    svg.interrupt();
    svg.call(zoomRef.current.transform, transform);
    lastZoomTargetRef.current = null;
  }, [pivotId, layout, width, height]);

  const handleZoomIn = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.interrupt();
    svg.call(zoomRef.current.scaleBy, 1.3);
    lastZoomTargetRef.current = null;
  }, []);

  const handleZoomOut = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.interrupt();
    svg.call(zoomRef.current.scaleBy, 0.7);
    lastZoomTargetRef.current = null;
  }, []);

  const handleZoomReset = useCallback(() => {
    if (!svgRef.current || !zoomRef.current || !layout) return;
    const points = collectBoundsPoints(layout);
    if (!points.length) return;
    const transform = computeFitTransform(points, width, height);
    const svg = d3.select(svgRef.current);
    svg.interrupt();
    svg.call(zoomRef.current.transform, transform);
    lastZoomTargetRef.current = null;
  }, [layout, width, height]);

  // Zoom to fit a specific set of points — toggle: click again to zoom back
  const zoomToPoints = useCallback((points, targetId) => {
    if (!svgRef.current || !zoomRef.current || !points.length) return;
    const svg = d3.select(svgRef.current);
    svg.interrupt();

    // If clicking the same target again, zoom back to fit-all
    if (targetId && lastZoomTargetRef.current === targetId) {
      lastZoomTargetRef.current = null;
      const allPoints = collectBoundsPoints(layout);
      if (allPoints.length) {
        const transform = computeFitTransform(allPoints, width, height);
        svg.call(zoomRef.current.transform, transform);
      }
      return;
    }

    lastZoomTargetRef.current = targetId || null;
    const transform = computeFitTransform(points, width, height);
    svg.call(zoomRef.current.transform, transform);
  }, [width, height, layout]);

  return { svgRef, zoomLevel, handleZoomIn, handleZoomOut, handleZoomReset, zoomToPoints };
}
